import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Award, Target, Eye, Users } from 'lucide-react';
import ScrollReveal from '@/components/animations/ScrollReveal';
import { useContent } from '@/hooks/useContent';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const { t } = useContent();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'About Us - ElhamyLabs';
  }, []);

  const timeline = [
    { year: t('about.journey.founded.year'), title: t('about.journey.founded.title'), description: t('about.journey.founded.description') },
    { year: t('about.journey.expansion.year'), title: t('about.journey.expansion.title'), description: t('about.journey.expansion.description') },
    { year: t('about.journey.digital.year'), title: t('about.journey.digital.title'), description: t('about.journey.digital.description') },
    { year: t('about.journey.innovation.year'), title: t('about.journey.innovation.title'), description: t('about.journey.innovation.description') },
  ];

  const values = [
    { icon: Award, title: t('about.values.excellence.title'), description: t('about.values.excellence.description') },
    { icon: Target, title: t('about.values.accuracy.title'), description: t('about.values.accuracy.description') },
    { icon: Eye, title: t('about.values.transparency.title'), description: t('about.values.transparency.description') },
    { icon: Users, title: t('about.values.care.title'), description: t('about.values.care.description') },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand-purple-dark via-brand-purple-base to-brand-purple-light dark:from-dark-primary dark:via-dark-secondary">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              {t('common.about')}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {t('about.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section ref={sectionRef} className="py-20 bg-light-primary dark:bg-dark-primary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <ScrollReveal direction="right">
              <div className="p-8 bg-light-secondary dark:bg-dark-secondary rounded-xl">
                <h2 className="text-3xl font-bold mb-4 text-light-text dark:text-dark-text">{t('about.mission.title')}</h2>
                <p className="text-light-text-secondary dark:text-dark-text-secondary">
                  {t('about.mission.description')}
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal direction="left">
              <div className="p-8 bg-light-secondary dark:bg-dark-secondary rounded-xl">
                <h2 className="text-3xl font-bold mb-4 text-light-text dark:text-dark-text">{t('about.vision.title')}</h2>
                <p className="text-light-text-secondary dark:text-dark-text-secondary">
                  {t('about.vision.description')}
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-light-secondary dark:bg-dark-secondary">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-center text-light-text dark:text-dark-text">{t('about.journey.title')}</h2>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-brand-purple-base/30 dark:bg-brand-orange-base/30"></div>
            {timeline.map((item, index) => (
              <ScrollReveal key={index} delay={index * 0.1}>
                <div className={`relative flex items-center mb-12 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="p-6 bg-light-primary dark:bg-dark-primary rounded-xl shadow-lg">
                      <div className="text-2xl font-bold text-brand-purple-base dark:text-brand-orange-base mb-2">{item.year}</div>
                      <h3 className="text-xl font-semibold mb-2 text-light-text dark:text-dark-text">{item.title}</h3>
                      <p className="text-light-text-secondary dark:text-dark-text-secondary">{item.description}</p>
                    </div>
                  </div>
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-brand-orange-base border-4 border-light-primary dark:border-dark-primary"></div>
                  <div className="w-1/2"></div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-light-primary dark:bg-dark-primary">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-center text-light-text dark:text-dark-text">{t('about.values.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="p-6 bg-light-secondary dark:bg-dark-secondary rounded-xl text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-purple-base/10 dark:bg-brand-orange-base/10 flex items-center justify-center">
                      <Icon size={32} className="text-brand-purple-base dark:text-brand-orange-base" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2 text-light-text dark:text-dark-text">{value.title}</h3>
                    <p className="text-light-text-secondary dark:text-dark-text-secondary">{value.description}</p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;

